import { Button, Flex, Grid } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useCallback, useContext } from 'react';

import DatePickerHeader from '@components/common/date/DatePickerHeader';
import DatePickerPager from '@components/common/date/DatePickerPager';
import DatePickerTextInput from '@components/common/date/DatePickerTextInput';
import { DatePickerReactContext } from '@components/common/date/context';
import { DatePickerProps } from '@components/common/date/types';
import { MONTHS, TODAY, shortMonthFormat } from 'helpers/dates';

const MonthPicker: React.FC<DatePickerProps> = ({
  onDateSelect,
  selected = TODAY,
  minDate,
  maxDate,
  hideYear = false,
  focused = true,
}) => {
  return (
    <DatePickerHeader onDateSelect={onDateSelect} selected={selected} pageBy="year">
      {!hideYear && (
        <DatePickerTextInput
          selected={selected}
          focused={focused}
          formatter={shortMonthFormat}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
      {!hideYear && (
        <Flex w="full" px="0.75rem">
          <DatePickerPager />
        </Flex>
      )}
      <MonthPickerBody selected={selected} minDate={minDate} maxDate={maxDate} />
    </DatePickerHeader>
  );
};
interface MonthPickerBodyProps {
  minDate: DateTime;
  maxDate: DateTime;
  selected: DateTime;
}

const MonthPickerBody: React.FC<MonthPickerBodyProps> = ({ selected, minDate, maxDate }) => {
  const { pagedYear, onDateSelect } = useContext(DatePickerReactContext);

  const isMonthDisabled = (month: number) => {
    const startOfMonth = DateTime.utc(pagedYear, month);
    return startOfMonth < minDate || startOfMonth > maxDate;
  };

  const onMonthSelect = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
      ev.stopPropagation();
      const month = Number(ev.currentTarget.value);
      const date = DateTime.utc(pagedYear, month);
      onDateSelect(date, { reason: 'button' });
    },
    [onDateSelect, pagedYear],
  );

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLButtonElement>) => {
      if (ev.key === 'Enter') {
        onMonthSelect(ev);
      }
    },
    [onMonthSelect],
  );

  return (
    <Grid
      flex="1 0"
      templateColumns="repeat(3, 1fr)"
      rowGap={1}
      columnGap={2}
      w="11.75rem"
      mt={0}
      data-testid="month-picker"
    >
      {MONTHS.map((month, index) => {
        if (index === 0) {
          return null;
        }

        const isSelected =
          selected != null && selected.month === index && selected.year === pagedYear;
        return (
          <Button
            key={month}
            data-testid={month}
            data-selected={isSelected || undefined}
            disabled={isMonthDisabled(index)}
            tabIndex={isSelected ? 0 : undefined}
            variant={isSelected ? 'subtle' : 'plain'}
            _selected={{ bg: 'surface.accent' }}
            size="sm"
            onMouseDown={onMonthSelect}
            onKeyDown={onKeyDown}
            value={index}
          >
            {month}
          </Button>
        );
      })}
    </Grid>
  );
};

export default React.memo(MonthPicker);
